import getLocal from 'services/getLocal';
import type { BasicSuccessResponse, HTTPFunctions } from '../types';
import type { AllWordBank, IntCompleteReport, LogRequest, LogResponse, MyWordBank } from 'pages/Analysis/types';
import type { AnalysisHandler, AnalysisResponse, FormDataAnalysisHandlerFile, SuggestionFlagRequest, SuggestionIssueResponse, SuggestionRequest } from 'pages/NewDocument/types';
import type { MyDocumentsResponse } from 'pages/MyDocuments/types';
import type { HomeData } from 'pages/Home/types';

const isLoggedIn = () => {
  return getLocal(localStorage).auth.getUser()?.access_token;
};

const analyze = ({ get, post, postFile, put, del, getPDF }: HTTPFunctions) => ({
  getSummary: () => get<HomeData>('/summary/'),
  getMyWordBank: () => get<AllWordBank>('/my-word-bank/'),
  addWordToMyWordBank: (word: string, isJargon: boolean, isKeyTerm: boolean) => post<MyWordBank>('/my-word-bank/', { word, is_jargon: isJargon, is_key: isKeyTerm }),
  updateWordInMyWordBankJargon: (word: string, isJargon: boolean) => put<MyWordBank>(`/my-word-bank/${word}`, { is_jargon: isJargon }),
  updateWordInMyWordBankKeyTerm: (word: string, isKeyTerm: boolean) => put<MyWordBank>(`/my-word-bank/${word}`, { is_key: isKeyTerm }),
  addListToMyWordBank: (data: File) => {
    const formData = new FormData();
    formData.append('csv_file', data);
    formData.append('type', 'text/csv');
    return postFile<AllWordBank>('/my-word-bank/upload', formData);
  },
  deleteWordFromMyWordBank: (word: string) => del<BasicSuccessResponse>(`/my-word-bank/${word}`),
  documentAnalysisText: (data: AnalysisHandler) =>
    post<AnalysisResponse>(isLoggedIn() ? '/analyze/text' : '/analyze/public/text', data),
  documentAnalysisPDF: (data: FormDataAnalysisHandlerFile) =>
    postFile<AnalysisResponse>(isLoggedIn() ? '/analyze/file' : '/analyze/public/file', data),
  setReportLabel: (data: {id: string, label: string}) =>
    put<BasicSuccessResponse>(`/my-documents/${data.id}?label=${data.label}`, {}),
  deleteDocument: (id: string) => del<BasicSuccessResponse>(`/my-documents/${id}`),
  downloadReport: (id: string) => getPDF<string>(`/my-documents/download/report/${id}`),
  downloadDocument: (id: string, typeDoc: string) =>
    getPDF<string>(`/my-documents/download/document/${id}/${typeDoc}`),
  documentAnalysisById: (id: string, fingerprint: string) =>
    get<IntCompleteReport>(isLoggedIn()? `/my-documents/${id}`: `/my-documents/public/${id}/${fingerprint}`),
  myDocuments: (page: number, owner: string, folder: string | undefined) => {
    if (owner === 'shared-with-me') {
      return get<MyDocumentsResponse>(`/analyze/shared-with-me/preview/page/${page}/11`);
    }
    if (!folder) {
      return get<MyDocumentsResponse>(`/my-documents/preview/page/${page}/11`);
    }
    return get<MyDocumentsResponse>(`/folder/preview/page/${folder}/${page}/11`);
  },
  myDocumentsCount: () => get<{count: number}>('/my-documents/count/'),
  addLog: (data: LogRequest) => post<LogResponse>(`/analyze/log`, data),
  getLog: (id: string, record: string) => get<LogResponse>(`/analyze/log/${record}/${id}`),
  reanalyze: (data: LogRequest) => post<IntCompleteReport>('/analyze/reanalize/original', data),
  reanalyzeByLogId: (id: string) => post<IntCompleteReport>(`/analyze/reanalize/log/${id}`, {}),
  getSuggestions: (data: SuggestionRequest) => post<SuggestionIssueResponse>('analyze/suggestions/', data),
  setSuggestionsFlag: (data: SuggestionFlagRequest) => put<BasicSuccessResponse>('analyze/suggestions/flag', data),
  documentsRebuild: (tigim: boolean) => post<BasicSuccessResponse>("/documents-rebuild/", { tigim }),
  getDocumentsRebuildStatus: () => get<string[]>("/documents-rebuild/status"),
  delDocumentsRebuildStatus: () => del<BasicSuccessResponse>("/documents-rebuild/status")
});

export default analyze;
